import { useCallback, useEffect, useRef, useState } from 'react'

const COPIED_INDICATOR_MS = 1600

/**
 * Generic copy-to-clipboard with a transient "copied" indicator, promoted
 * from the referral share hook's pattern. `copied` holds the id (or the text
 * itself) of the last copy for COPIED_INDICATOR_MS, so a surface with several
 * copy buttons can flip only the one that was clicked.
 */
export function useCopyToClipboard() {
  const [copied, setCopied] = useState<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    },
    []
  )

  const copy = useCallback(async (text: string, id?: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(id ?? text)
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => setCopied(null), COPIED_INDICATOR_MS)
      return true
    } catch (err) {
      console.error('Copy to clipboard failed:', err)
      return false
    }
  }, [])

  return { copied, copy }
}
